import { useTranslation } from 'react-i18next';

import { LocaleLink } from '../../components/navigation/LocaleLink';
import heroImage from '../../assets/hero-image-960.webp';

/**
 * "Infinite Adventure Awaits in SJ" — spec §7.1. A two-column editorial block (image + lead
 * copy) over a warm gradient CTA banner into Activities. Static marketing copy (i18n), not an
 * admin list.
 */
export function InfiniteBanner() {
  const { t } = useTranslation();

  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      <div className="grid items-center gap-8 md:grid-cols-2">
        {/*
          The 960px variant, not the hero's 1920: this renders in a half-width column that never
          exceeds ~640 CSS px. Lazy, because the block sits well below the fold — the opposite
          call from the hero, which must not be lazy (§17).
        */}
        <img
          src={heroImage}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className="aspect-[4/3] w-full rounded-3xl object-cover"
        />
        <div>
          <h2 className="text-3xl font-bold leading-tight text-content sm:text-4xl">
            {t('home.infiniteHeadline')} <span className="text-accent">{t('home.infiniteHeadlineAccent')}</span>
          </h2>
          <p className="mt-4 leading-relaxed text-content-muted">{t('home.infiniteBody')}</p>
        </div>
      </div>

      {/* Warm gradient CTA banner. */}
      {/*
        Sunset gradient at the -700 stops, not -500 (§17 contrast).
        The white copy over this banner is 18px/600 — not "large text" by WCAG, so it needs
        4.5:1. Against amber-500 it had 2.15:1 and against rose-500 3.67:1: the whole gradient
        failed, the amber end badly. The -700 stops give 5.02 / 5.18 / 6.29 while keeping the
        sunset reading the design (§25.5) asks for.
      */}
      <div className="mt-8 flex flex-col gap-4 overflow-hidden rounded-3xl bg-gradient-to-r from-amber-700 via-orange-700 to-rose-700 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
        <div className="text-white">
          <p className="text-lg font-semibold">{t('home.infiniteCtaTitle')}</p>
          <p className="text-sm text-white/80">{t('home.infiniteCtaSubtitle')}</p>
        </div>
        <LocaleLink
          to="/what-to-do/activities-and-tours"
          className="inline-flex shrink-0 items-center gap-2 self-start rounded-pill bg-white px-6 py-3 text-sm font-semibold text-ink transition-opacity hover:opacity-90 sm:self-auto"
        >
          {t('home.learnMore')} →
        </LocaleLink>
      </div>
    </section>
  );
}
